import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ImportMenu.css';
import { checkAuth, authenticatedRequest } from '../utils/auth';

const ImportMenu = () => {
  const [menuUrl, setMenuUrl] = useState('');
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuError, setMenuError] = useState('');
  const [menuData, setMenuData] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurantId, setSelectedRestaurantId] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [categories, setCategories] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const initializeComponent = async () => {
      const isAuthenticated = await checkAuth();
      if (!isAuthenticated) {
        navigate('/');
        return;
      }
      await fetchRestaurants();
    };
    
    initializeComponent();
  }, [navigate]);

  const fetchRestaurants = async () => {
    setIsLoading(true);
    try {
      const response = await authenticatedRequest('GET', '/restaurants-public');
      
      if (response.data && response.data.restaurants) {
        setRestaurants(response.data.restaurants);
      } else {
        console.error('Error fetching restaurants:', response.data?.message);
        setError('خطا در دریافت لیست رستوران‌ها');
      }
    } catch (err) {
      console.error('Error fetching restaurants:', err);
      setError('خطا در اتصال به سرور برای دریافت رستوران‌ها');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRestaurantChange = (e) => {
    setSelectedRestaurantId(e.target.value);
  };

  // تابع استخراج ID از URL اسنپ‌فود
  const extractSnappfoodId = (url) => {
    const regex = /-r-([a-zA-Z0-9]+)/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  // تابع دریافت منو از API اسنپ‌فود
  const fetchSnappfoodMenu = async () => {
    setMenuLoading(true);
    setMenuError('');
    try {
      const restaurantId = extractSnappfoodId(menuUrl);
      if (!restaurantId) {
        throw new Error('آدرس منو نامعتبر است');
      }

      // استفاده از AllOrigins به عنوان CORS proxy
      const proxyUrl = 'https://api.allorigins.win/get?url=';
      const targetUrl = encodeURIComponent(`https://apigw.snappfood.ir/menu-read-model/${restaurantId}`);
      
      const response = await axios.get(proxyUrl + targetUrl);
      
      // AllOrigins داده را در فرمت متفاوتی برمی‌گرداند، پس باید آن را پردازش کنیم
      const menuData = JSON.parse(response.data.contents);
      setMenuData(menuData);

      // استخراج دسته‌بندی‌های منو به صورت یکتا
      const uniqueCategories = [...new Set(
        menuData.data.menuCategories?.flatMap(category => 
          category.products?.map(product => product.menuCategoryTitle) || []
        ) || []
      )].filter(Boolean); // حذف مقادیر null یا undefined
      console.log('Unique Categories:', uniqueCategories); // برای دیباگ
      setCategories(uniqueCategories);



    } catch (error) {
      setMenuError('خطا در دریافت منو. لطفاً لینک را بررسی کنید.');
      console.error('Error fetching menu:', error);
    } finally {
      setMenuLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedRestaurantId) {
      setError('لطفاً یک رستوران را انتخاب کنید');
      return;
    }

    if (!menuData) {
      setMenuError('لطفاً ابتدا منو را از اسنپ‌فود دریافت کنید');
      return;
    }

    setIsSubmitting(true);
    try {
      // آماده‌سازی داده‌ها برای ارسال به بک‌اند
      const menuItems = menuData.data.menuCategories.flatMap(category => 
        category.products?.map(item => ({
          name: item.title,
          description: item.description || "",
          price: item.variations?.[0]?.price || 0,
          image: item.images?.[0]?.src || "",
          categoryName: item.menuCategoryTitle,
          subcategory: item.menuCategoryTitle // اضافه کردن فیلد subcategory با مقدار menuCategoryTitle
        })) || []
      );

      const dataToSend = {
        restaurantId: selectedRestaurantId,
        categories: categories,
        menuItems: menuItems
      };

      // ارسال به بک‌اند با استفاده از authenticatedRequest
      const response = await authenticatedRequest('POST', '/import-menu', dataToSend);
      
      if (response.data.ok) {
        setSuccess(response.data.message || 'منو با موفقیت ذخیره شد');
      } else {
        setError('خطا در ذخیره منو: ' + (response.data.message || 'خطای ناشناخته'));
      }
    } catch (error) {
      setError('خطا در ارسال اطلاعات به سرور');
      console.error('Error submitting menu:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMenuUrlChange = (e) => {
    setMenuUrl(e.target.value);
  };

  return (
    <div className="import-menu-container">
      <h2>وارد کردن منو از اسنپ‌فود</h2>
      
      <div className="restaurant-section">
        <label htmlFor="restaurant">انتخاب رستوران:</label>
        <select
          id="restaurant"
          value={selectedRestaurantId}
          onChange={handleRestaurantChange}
          className="restaurant-dropdown"
          disabled={isLoading}
        >
          <option value="">
            {isLoading ? 'در حال بارگذاری...' : 'انتخاب رستوران از لیست'}
          </option>
          {!isLoading && restaurants.map(restaurant => (
            <option key={restaurant._id} value={restaurant._id}>
              {restaurant.name}
            </option>
          ))}
        </select>
        {error && <div className="error-message">{error}</div>}
      </div>

      <div className="menu-input-section">
        <label htmlFor="menuUrl">آدرس صفحه رستوران در اسنپ‌فود:</label>
        <input
          type="text"
          id="menuUrl"
          value={menuUrl}
          onChange={handleMenuUrlChange}
          placeholder="مثال: https://snappfood.ir/restaurant/menu/xyz-r-abc123"
        />
        <button 
          onClick={fetchSnappfoodMenu}
          disabled={menuLoading}
        >
          {menuLoading ? 'در حال دریافت منو...' : 'دریافت منو'}
        </button>
      </div>

      {menuError && (
        <div className="error-message">
          {menuError}
        </div>
      )}

      {success && (
        <div className="success-message">
          {success}
        </div>
      )}

      {menuData && categories.length > 0 && (
        <div className="menu-categories">
          <h3>دسته‌بندی‌های منو:</h3>
          <ul>
            {categories.map((category, index) => (
              <li key={index}>{category}</li>
            ))}
          </ul>
          <button 
            onClick={handleSubmit}
            disabled={isSubmitting}
            className="submit-button"
          >
            {isSubmitting ? 'در حال ذخیره منو...' : 'ذخیره منو'}
          </button>
        </div>
      )}

      {menuData && (
        <div className="menu-preview">
          <h3>پیش‌نمایش منو</h3>
          {menuData.data.menuCategories?.map((category, categoryIndex) => (
            <div key={categoryIndex} className="menu-category">
              <h4>{category.name}</h4>
              <div className="menu-items">
                {category.products?.map((item, itemIndex) => (
                  <div key={itemIndex} className="menu-item">
                    {item.images?.[0]?.src && (
                      <img src={item.images[0].src} alt={item.title} className="menu-item-image" />
                    )}
                    <div className="menu-item-details">
                      <h5>{item.title}</h5>
                      <p className="description">{item.description}</p>
                      <p className="price">
                        {item.variations?.[0]?.price?.toLocaleString()} تومان
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImportMenu;